import { useEffect, useState } from "react";
import { Trash2, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import ConfirmModal from "./ConfirmModal";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

export default function FloatingCart() {
  const navigate = useNavigate();
  const location = useLocation();
  const { items, totals, updateQty, removeItem, clearCart, isOpen, setIsOpen } = useCart();
  const [confirmClear, setConfirmClear] = useState(false);

  const hideOn = ["/checkout", "/payment", "/cart"];
  const isHidden = hideOn.includes(location.pathname);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, setIsOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const subtotal = totals.subtotal;
  const count = totals.count;
  const hasItems = items.length > 0;

  const goCheckout = () => {
    if (!hasItems) return;
    navigate("/checkout");
  };

  if (isHidden || !hasItems) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={[
          "fixed bottom-5 right-5 z-50",
          "flex items-center gap-2 rounded-full border border-black/15 bg-white px-3 py-2 text-sm font-semibold text-black",
          "shadow-[0_10px_30px_rgba(0,0,0,0.18)]",
          "transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_14px_38px_rgba(0,0,0,0.24)]",
        ].join(" ")}
        aria-label="Open cart"
      >
        <span className="text-base">🛒</span>
        <span>Cart</span>

        {count > 0 && (
          <span className="ml-1 rounded-full bg-[#1c1c1c] px-2 py-[2px] text-xs font-bold text-white">
            {count}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        className={[
          "fixed right-0 top-0 z-50 flex h-[100dvh] w-full max-w-md flex-col bg-white text-black shadow-2xl",
          "transition-transform duration-200",
          isOpen ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-label="Cart drawer"
      >
        <div className="flex items-center justify-between border-b border-black/10 px-5 py-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-black/60">
              Cart
            </p>
            <h3 className="text-lg font-semibold">
              {hasItems ? `${count} item${count > 1 ? "s" : ""}` : "Empty"}
            </h3>
          </div>

          <div className="flex items-center gap-4">
            {hasItems && (
              <button
                type="button"
                onClick={() => setConfirmClear(true)}
                aria-label="Clear cart"
                title="Clear cart"
                className="flex h-9 w-9 items-center justify-center rounded-full text-black/50 transition hover:bg-pink-50 hover:text-pink-600"
              >
                <Trash2 size={18} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-2xl border border-black/10 px-3 py-2 text-sm transition hover:border-black/25"
              aria-label="Close cart"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="h-[calc(100%-176px)] overflow-auto px-5 py-4">
          {!hasItems ? (
            <div className="rounded-3xl border border-black/10 bg-black/[0.03] p-6">
              <p className="font-semibold">Your cart is empty</p>
              <p className="mt-2 text-sm text-black/60">
                Add frames to your cart to checkout.
              </p>
              <Link
                to="/shop"
                className="mt-4 inline-flex items-center justify-center rounded-2xl bg-pink-500 px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
              >
                Shop Frames
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((x) => {
                const unitPrice = Number(x.final_price ?? x.price ?? 0);
                const selectedOptions = Array.isArray(x.selected_options)
                  ? x.selected_options
                  : [];
                const uploadedFiles = Array.isArray(x.uploaded_files)
                  ? x.uploaded_files
                  : [];
                const customText =
                  typeof x.custom_text === "string" ? x.custom_text.trim() : "";

                return (
                  <div
                    key={x.key}
                    className="rounded-3xl border border-black/10 bg-white p-4"
                  >
                    <div className="flex gap-3">
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-black/[0.04]">
                        {x.image ? (
                          <img
                            src={x.image}
                            alt={x.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : null}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-semibold">{x.name}</p>

                            {selectedOptions.length > 0 && (
                              <div className="mt-1 space-y-1">
                                {selectedOptions.map((option, index) => (
                                  <p
                                    key={`${option.id || option.label || index}_${index}`}
                                    className="text-xs text-black/60"
                                  >
                                    <span className="font-medium text-black/90">{option.group_name || 'Option'}:</span> {option.label}
                                    {Number(option.price) > 0 ? ` (+${money(option.price)})` : ""}
                                  </p>
                                ))}
                              </div>
                            )}

                            {customText && (
                              <div className="mt-2">
                                <p className="text-[10px] uppercase text-black/40">
                                  Custom text
                                </p>
                                <p className="text-xs text-black/70 whitespace-pre-wrap">
                                  {customText}
                                </p>
                              </div>
                            )}

                            {uploadedFiles.length > 0 && (
                              <div className="mt-2">
                                <p className="mb-2 text-[10px] uppercase text-black/40">
                                  Uploaded image
                                  {uploadedFiles.length > 1 ? "s" : ""}
                                </p>

                                <div className="flex flex-wrap gap-2">
                                  {uploadedFiles.map((file, index) => {
                                    const preview =
                                      file?.preview ||
                                      (typeof file === "string" ? file : "");

                                    if (!preview) return null;

                                    return (
                                      <div
                                        key={`${file?.name || "upload"}_${index}`}
                                        className="h-10 w-10 overflow-hidden rounded-lg border border-black/10"
                                      >
                                        <img
                                          src={preview}
                                          alt={`Upload ${index + 1}`}
                                          className="h-full w-full object-cover"
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() => removeItem(x.key)}
                            aria-label="Remove item"
                            title="Remove item"
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-black/40 transition hover:bg-pink-50 hover:text-pink-600"
                          >
                            <X size={16} />
                          </button>
                        </div>

                        <div className="mt-3 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-black/60">Qty</span>
                            <input
                              type="number"
                              min={1}
                              value={x.qty}
                              onChange={(e) =>
                                updateQty(x.key, e.target.value)
                              }
                              className="w-16 rounded-xl border border-black/15 px-2 py-1 text-sm outline-none focus:border-black/35"
                            />
                          </div>

                          <p className="font-semibold">
                            {money(unitPrice * Number(x.qty || 1))}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

            </div>
          )}
        </div>

        <div className="border-t border-black/10 px-5 py-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-black/60">Subtotal</span>
            <span className="text-lg font-semibold">{money(subtotal)}</span>
          </div>

          <div className="mt-4 grid gap-3">
            <button
              type="button"
              onClick={goCheckout}
              disabled={!hasItems}
              className={[
                "rounded-2xl px-5 py-3 text-sm font-semibold transition",
                hasItems
                  ? "bg-[#1c1c1c] text-white hover:opacity-90"
                  : "cursor-not-allowed bg-black/30 text-white",
              ].join(" ")}
            >
              Checkout
            </button>
          </div>
        </div>
      </div>
      <ConfirmModal
        open={confirmClear}
        title="Clear your cart?"
        message="This will remove all items from your cart."
        confirmLabel="Clear cart"
        onConfirm={() => {
          clearCart();
          setConfirmClear(false);
        }}
        onCancel={() => setConfirmClear(false)}
      />
    </>
  );
}